import { Accordion, AccordionDetails, AccordionSummary, Chip, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@nutrien/cxp-components';
import React, { useCallback, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';

import type {
  AssetsByAreaHeirachy,
  HeirachyResult,
} from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import type {
  AssetSectionDefinationsList,
  AssetSectionDefinationsListDocument,
  DynamicProperties,
  Lov,
} from '../../rxdb/assetSectionDefination/assetSectionDefinition.types';
import { useAssetSectionsDefinations } from '../../rxdb/assetSectionDefination/useAssetSectionDefinations';
import type {
  AssetArea,
  AssetSection,
  AssetSectionArea,
  AssetSectionMetalicPropertyValue,
  AssetSectionPropertyValue,
  LineandSectionPropertiesListDocument,
  SectionCmlattribute,
} from '../../rxdb/lineandSectionPropertiesDetails/lineandSectionPropertiesDetails.types';
import LineAndSectionSidePanel from '../LineAndSectionSidePanel';

interface Props {
  lineandSectionPropertiesListDocument: LineandSectionPropertiesListDocument[];
  assetsByAreaHierarchyData: AssetsByAreaHeirachy[];
  isPiping: boolean;
}

const useStyles = makeStyles({
  root: {
    height: 'calc(100vh - 289px)',
    overflowY: 'auto',
  },
  card: {
    padding: '16px',
  },
  areaPcsId: {
    fontWeight: 'normal',
    color: 'rgba(255, 255, 255, .38)',
  },
  cardViewData: {
    paddingBottom: 12,
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '0px',
  },
  cardViewDataGrid: {
    display: 'flex',
    flexDirection: 'row',
  },
  cardViewDataName: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 16,
    letterSpacing: '0.5px',
    fontWeight: 400,
    fontStyle: 'normal',
  },
  cardViewDataValue: {
    color: 'rgba(255, 255, 255, 0.87)',
    fontWeight: 'normal',
    fontSize: 16,
    letterSpacing: '0.5px',
  },
  cardViewImageDataValue: {
    color: '#6CD9C7',
    fontWeight: 'normal',
    fontSize: 16,
    letterSpacing: '0.5px',
  },
  chip: {
    marginRight: '8px',
    cursor: 'pointer',
    '&.MuiChip-outlined': {
      color: '#6CD9C7',
      borderColor: '#6CD9C7',
      backgroundColor: 'rgba(39, 126, 126, 0.10)',
    },
  },
  lineAndSectionContainer: {
    width: '100%',
  },
  propertiesContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginBottom: '2px',
    padding: '6px',
  },
  mainGrid: {
    marginTop: 16,
    marginBottom: 1,
    borderRadius: '10px 10px 0px 0px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  titleText: {
    padding: '8.5px 0px 7.5px 4px',
    fontStyle: 'normal',
  },
});

const LineAndSectionProperties: React.FC<Props> = ({
  lineandSectionPropertiesListDocument,
  assetsByAreaHierarchyData,
  isPiping,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const data = lineandSectionPropertiesListDocument[0]?.assetSection;
  const areas = lineandSectionPropertiesListDocument[0]?.assetArea;
  const [cubeArea, setCubeArea] = useState<AssetsByAreaHeirachy[]>([]);
  const [sectionData, setSectionData] = useState<AssetSectionDefinationsList[]>([]);
  const [sectionCmlattributeData, setSectionCmlattributeData] = useState<SectionCmlattribute>([]);
  const [assetSectionDefinationsList, setAssetSectionDefinationsList] = useState<
    AssetSectionDefinationsList[]
  >([]);
  const {
    assetSectionDefinationsListInitialized,
    getAssetSectionDefinationsList,
    assetSectionDefinationsListCollection,
  } = useAssetSectionsDefinations();
  const [assetSectionDefinationsSubscription, setAssetSectionDefinationsSubscription] =
    useState<Subscription>();
  const fieldDetails = [
    {
      label: 'D-OD of Pipe (in)',
      id: 'dOd',
    },
    {
      label: 'E - Longitudinal Quality Factor (joint efficiency)',
      id: 'eLongitudeQualityFactor',
    },
    {
      label: 'Design Corrosion Allowance (in)',
      id: 'designCorrosionAllowance',
    },
    {
      label: 'P - Internal design gauge pressure of the pipe (psig)',
      id: 'pInternalDesignGause',
    },
  ];

  const getAssetSectionDefinationsFromRxdb = async () => {
    if (assetSectionDefinationsListCollection) {
      const assetSectionDefinationsListDocument: AssetSectionDefinationsListDocument[] =
        await getAssetSectionDefinationsList();
      if (assetSectionDefinationsListDocument) {
        setAssetSectionDefinationsList(
          JSON.parse(JSON.stringify(assetSectionDefinationsListDocument)),
        );
      }
    }
  };

  const subscribeToAssetSectionDefinations = async () => {
    if (assetSectionDefinationsListInitialized && assetSectionDefinationsListCollection) {
      const assetSectionDefinationsSub = assetSectionDefinationsListCollection
        .find()
        .$.subscribe(() => {
          getAssetSectionDefinationsFromRxdb();
        });
      setAssetSectionDefinationsSubscription(assetSectionDefinationsSub);
    }
  };

  const callback = useCallback((data1: boolean) => {
    setOpen(data1);
  }, []);

  useEffect(() => {
    if (assetSectionDefinationsListInitialized) {
      getAssetSectionDefinationsFromRxdb();
      subscribeToAssetSectionDefinations();
    }
    return () => {
      if (assetSectionDefinationsSubscription) assetSectionDefinationsSubscription.unsubscribe();
    };
  }, [assetSectionDefinationsListInitialized]);

  useEffect(() => {
    const difference = assetSectionDefinationsList.filter((item: AssetSectionDefinationsList) =>
      data.find((item2: AssetSection) => Number(item.id) === item2.fkSectionDefinition),
    );
    setSectionData(difference);
  }, [assetSectionDefinationsList]);

  useEffect(() => {
    const assetsByAreaData: AssetsByAreaHeirachy[] = JSON.parse(
      JSON.stringify(assetsByAreaHierarchyData),
    );
    const areasList = assetsByAreaData.filter((item: AssetsByAreaHeirachy) =>
      areas.find((item2: AssetArea) => Number(item.type) === item2.layer),
    );
    setCubeArea(areasList);
  }, [assetsByAreaHierarchyData]);

  const chip = (val: string) => {
    return <Chip key={val} variant="outlined" label={val} className={classes.chip} />;
  };

  const showArea = (assetSectionArea: AssetSectionArea[]) => {
    const location: string[] = [];
    const childItem = cubeArea[0]?.children;
    for (const k in childItem) {
      const childrenItem = childItem[k].children;
      childrenItem.map((child1Item: HeirachyResult) => {
        assetSectionArea.map((assetSectionAreaItem: AssetSectionArea) => {
          if (
            assetSectionAreaItem.fkArea === child1Item.id &&
            !location.includes(child1Item.areaPcsId)
          )
            location.push(child1Item.areaPcsId);
          else {
            child1Item.children.map((child2Item: HeirachyResult) => {
              if (
                child2Item.id === assetSectionAreaItem.fkArea &&
                !location.includes(child2Item.areaPcsId)
              )
                location.push(child2Item.areaPcsId);
            });
          }
        });
      });
    }
    return (
      <Grid item container xs={12} className={classes.cardViewData}>
        <Grid item xs={12} className={classes.cardViewDataGrid}>
          <Grid item xs={4}>
            <Typography variant="body1" className={classes.cardViewDataName}>
              Cubes
            </Typography>
          </Grid>
          {location?.length > 0 && location.map(item => <Grid item>{chip(item)}</Grid>)}
        </Grid>
      </Grid>
    );
  };

  const showData = (item: AssetSection) => {
    const assetSectionPropertyValue = item.assetSectionPropertyValue;
    const sectionDataObj = sectionData.find(
      (Sdta: AssetSectionDefinationsList) => item.fkSectionDefinition?.toString() === Sdta.id,
    );
    const dynamicProperties =
      sectionDataObj?.dynamicProperties || ([] as unknown as [DynamicProperties]);
    const dynamicPropertiesList = dynamicProperties.map((Sdta1: DynamicProperties) => {
      const propertyValue = assetSectionPropertyValue.find(
        (propertyItem: AssetSectionPropertyValue) =>
          propertyItem.fkSectionPropertyDefinition === Sdta1.id,
      );
      const getDisplayValue = () => {
        if (propertyValue) {
          return Sdta1?.lOV?.length > 0
            ? Sdta1.lOV.find((lovItem: Lov) => lovItem.value === Number(propertyValue.value))
                ?.display || ''
            : propertyValue.value;
        }
        return '';
      };
      if (propertyValue) {
        return {
          label: Sdta1.name,
          displayValue: ['Insulated', 'Heat Traced'].includes(Sdta1.name)
            ? getDisplayValue() === 'true'
              ? 'Yes'
              : 'No'
            : getDisplayValue(),
        };
      } else {
        return {
          label: Sdta1.name,
          displayValue: '-',
        };
      }
    });
    const dynamicPropertiesListFiltered = dynamicPropertiesList.filter(
      property => property.displayValue !== '-',
    );
    const half = Math.ceil(dynamicPropertiesListFiltered?.length / 2);
    const firstHalf = dynamicPropertiesListFiltered?.slice(0, half);
    const secondHalf = dynamicPropertiesListFiltered?.slice(
      half,
      assetSectionPropertyValue?.length,
    );
    return (
      <Grid container>
        <Grid container xs={6} className="firstHalf">
          {firstHalf.map(property => {
            return (
              <>
                <Grid item container xs={12} className={classes.cardViewData}>
                  <Grid item xs={12} className={classes.cardViewDataGrid}>
                    <Grid item xs={8}>
                      <Typography variant="body1" className={classes.cardViewDataName}>
                        {property.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.cardViewDataValue}>
                        {property.displayValue}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            );
          })}
        </Grid>
        <Grid container xs={6} className="secondHalf">
          {secondHalf.map(property => {
            return (
              <Grid item container xs={12} className={classes.cardViewData}>
                <Grid item xs={12} className={classes.cardViewDataGrid}>
                  <Grid item xs={8}>
                    <Typography variant="body1" className={classes.cardViewDataName}>
                      {property.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body1" className={classes.cardViewDataValue}>
                      {property.displayValue}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  const showMetalicVlaue = (assetSectionMetalicProperty: AssetSectionMetalicPropertyValue[]) => {
    return (
      assetSectionMetalicProperty?.length > 0 && (
        <Grid container>
          <Grid container xs={6} className="firstHalf">
            {fieldDetails.map(
              fields =>
                assetSectionMetalicProperty[0][
                  fields.id as keyof AssetSectionMetalicPropertyValue
                ] > 0 && (
                  <Grid item container xs={12} className={classes.cardViewData}>
                    <Grid item xs={12} className={classes.cardViewDataGrid}>
                      <Grid item xs={8}>
                        <Typography variant="body1" className={classes.cardViewDataName}>
                          {fields.label}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body1" className={classes.cardViewDataValue}>
                          {
                            assetSectionMetalicProperty[0][
                              fields.id as keyof AssetSectionMetalicPropertyValue
                            ]
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ),
            )}
          </Grid>
        </Grid>
      )
    );
  };

  const showSidePanel = (sectionCmlattribute: SectionCmlattribute) => {
    setSectionCmlattributeData(sectionCmlattribute);
    setOpen(true);
  };

  return (
    <Grid className={`${classes.card} ${classes.mainGrid}`}>
      <Grid item xs={6}>
        <Typography variant="h5" className={classes.titleText}>
          Line/Section Properties
        </Typography>
      </Grid>
      <div>
        <div>
          {data.map((item: AssetSection) => {
            return (
              <Accordion>
                <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
                  <Typography>{item.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid className={classes.lineAndSectionContainer}>
                    <Grid container className={classes.propertiesContainer}>
                      {item.description && (
                        <Grid item container xs={12} className={classes.cardViewData}>
                          <Grid item xs={12} className={classes.cardViewDataGrid}>
                            <Grid item xs={4}>
                              <Typography variant="body1" className={classes.cardViewDataName}>
                                Description
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body1" className={classes.cardViewDataValue}>
                                {item.description}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      <Grid item container>
                        {item.fromMajorAsset && (
                          <Grid item container xs={12} className={classes.cardViewData}>
                            <Grid item xs={12} className={classes.cardViewDataGrid}>
                              <Grid item xs={4}>
                                <Typography variant="body1" className={classes.cardViewDataName}>
                                  From Major Asset
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="body1" className={classes.cardViewDataValue}>
                                  {item.fromMajorAsset}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}
                        {item.toMajorAsset && (
                          <Grid item container xs={12} className={classes.cardViewData}>
                            <Grid item xs={12} className={classes.cardViewDataGrid}>
                              <Grid item xs={4}>
                                <Typography variant="body1" className={classes.cardViewDataName}>
                                  To Major Asset
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="body1" className={classes.cardViewDataValue}>
                                  {item.toMajorAsset}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item container xs={12} className={classes.cardViewData}>
                        {showArea(item.assetSectionArea)}
                      </Grid>
                      {item.ksi !== null && isPiping && (
                        <Grid item container xs={12} className={classes.cardViewData}>
                          <Grid item xs={12} className={classes.cardViewDataGrid}>
                            <Grid item xs={4}>
                              <Typography variant="body1" className={classes.cardViewDataName}>
                                S(K SI)
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body1" className={classes.cardViewDataValue}>
                                {item.ksi}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {item.temperature !== null && isPiping && (
                        <Grid item container xs={12} className={classes.cardViewData}>
                          <Grid item xs={12} className={classes.cardViewDataGrid}>
                            <Grid item xs={4}>
                              <Typography variant="body1" className={classes.cardViewDataName}>
                                TEMPERATURE (*F)
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="body1" className={classes.cardViewDataValue}>
                                {item.temperature}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <Grid container className={classes.propertiesContainer}>
                      {showData(item)}
                    </Grid>
                    <Grid container className={classes.propertiesContainer}>
                      {showMetalicVlaue(item.assetSectionMetalicPropertyValue)}
                    </Grid>

                    <Grid container className={classes.propertiesContainer}>
                      <Grid item container xs={12} className={classes.cardViewData}>
                        <Grid item xs={12} className={classes.cardViewDataGrid}>
                          <Grid item xs={4}>
                            <Typography variant="body1" className={classes.cardViewDataName}>
                              CML
                            </Typography>
                          </Grid>
                          {item.sectionCmlattribute &&
                            item.sectionCmlattribute.map((att: SectionCmlattribute) => {
                              return (
                                <Grid item xs={2} onClick={() => showSidePanel(att)}>
                                  {chip(att.cmlcode)}
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>
      <LineAndSectionSidePanel
        open={open}
        sectionCmlattribute={sectionCmlattributeData}
        Closecallback={callback}
        cubeArea={cubeArea[0]}
      />
    </Grid>
  );
};

export default LineAndSectionProperties;
